import { type ChangeEvent, type FormEvent, memo, useCallback, useState } from 'react';

import Sort from '@/components/Sort';
import { DIRECTION } from '@/types/sort';
import InputField from '@/ui/components/Form/InputField';
import type { OptionType, defaultOptionTypes } from '@/ui/components/Form/Select/types';

import type { Props } from './types';

export const allPeopleDefaultSearchParams = {
  direction: DIRECTION.DESC,
  ordering: 'created_at',
  page: '1',
  search: ''
};

const allPeopleSortOptions = [
  { label: 'First Name', value: 'first_name' },
  { label: 'Last Name', value: 'last_name' },
  { label: 'App open date', value: 'last_app_open_at' },
  { label: 'Creation date', value: 'created_at' }
];

const AllPeopleFilterForm = ({ activeParams, setParams }: Props) => {
  const [search, setSearch] = useState<string>(activeParams.search);

  const handleSortChange = useCallback(
    (option: OptionType<defaultOptionTypes>) => {
      if (option.value)
        setParams((currentParams) => {
          if (option.value !== allPeopleDefaultSearchParams.ordering)
            currentParams.set('ordering', option.value as string);
          else currentParams.delete('ordering');
          currentParams.delete('page');
          return currentParams;
        });
    },
    [setParams]
  );

  const handleSortDirectionChange = useCallback(() => {
    setParams((currentParams) => {
      const [nonDefaultDirection] = Object.values(DIRECTION).filter(
        (direction) => direction !== allPeopleDefaultSearchParams.direction
      );
      if (activeParams.direction === allPeopleDefaultSearchParams.direction)
        currentParams.set('direction', nonDefaultDirection);
      else currentParams.delete('direction');
      currentParams.delete('page');
      return currentParams;
    });
  }, [activeParams.direction, setParams]);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value),
    []
  );

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    setParams((currentParams) => {
      if (search) currentParams.set('search', search);
      else currentParams.delete('search');
      return currentParams;
    });
  };

  return (
    <form onSubmit={handleSearchSubmit}>
      <div className="flex items-center divide-x text-sm mb-1">
        <div className="flex-grow pr-2">
          <InputField
            id="search"
            placeholder="Search by name, email, phone or ID"
            type="text"
            typeHelper="search"
            value={search}
            onChange={handleSearchChange}
            wrapperClassNames="w-full"
          />
        </div>
        <div className="pl-2">
          <Sort
            sortOptions={allPeopleSortOptions}
            ascending={activeParams.direction === DIRECTION.ASC}
            value={activeParams.ordering}
            onChange={handleSortChange}
            onDirectionChange={handleSortDirectionChange}
          />
        </div>
      </div>
    </form>
  );
};

export default memo(AllPeopleFilterForm);
